import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { url: String };

  connect() {
    if (!this.modalElement) {
      this.createModal();
    }
    this.initialContentHeight = 0;
  }

  disconnect() {
  }

  createModal() {
    const modal = document.createElement("div");
    modal.id = "global-modal";
    modal.classList.add("forecast-modal");
    modal.innerHTML = '<div class="forecast-modal-content"></div>';
    document.body.appendChild(modal);
  }

  open(event) {
    event.preventDefault();
    // Masquer le panel de session avant d'ouvrir la modal
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      this.hideSessionPanel();
    }
    this.fetchAndDisplayModal(this.urlValue || event.currentTarget.href);
  }

  quick_update(event) {
    event.preventDefault();
    this.fetchAndDisplayModal(event.currentTarget.href);
  }

  // Méthode pour masquer le panel de session
  hideSessionPanel() {
    const sessionElement = document.querySelector('#session');
    if (sessionElement && this.isSessionPanelVisible() && typeof window.remove_panel === 'function') {
      window.remove_panel(sessionElement);
    }
  }

  // Méthode utilitaire pour vérifier si le panel de session est visible
  isSessionPanelVisible() {
    const sessionElement = document.querySelector('#session');
    return sessionElement &&
      (sessionElement.classList.contains('slide-right') ||
       sessionElement.style.display === 'flex');
  }

  fetchAndDisplayModal(url) {
    if (!this.modalElement) {
      console.error("Modal not found");
      return;
    }

    this.fetchModalContent(url)
      .then((html) => {
        this.modalContentElement.innerHTML = html;
        this.modalElement.classList.add("open");

        // Récupérer les coordonnées depuis les data attributes
        const spotCoordinates = this.modalContentElement.querySelector(
          "[data-spot-coordinates]"
        );
        if (spotCoordinates && window.map) {
          // Attendre que la modal soit complètement rendue avant d'animer la carte
          setTimeout(() => {
            this.animateMapToSpot(spotCoordinates);
          }, 100); // Petit délai pour éviter les conflits de rendu
        }

        // Attendez que le contenu soit rendu
        setTimeout(() => {
          this.setInitialContentHeight();
          this.addTabListeners();
        }, 0);
      })
      .catch((error) => console.error("Error fetching modal content:", error));
  }

  fetchModalContent(url) {
    return fetch(url, {
      method: "GET",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/html",
      },
      credentials: "same-origin",
    }).then((response) => response.text());
  }

  get modalElement() {
    return document.getElementById("global-modal");
  }

  get modalContentElement() {
    return this.modalElement?.querySelector(".forecast-modal-content");
  }

  get csrfToken() {
    return document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
  }

  setInitialContentHeight() {
    const previsionsTab = this.modalElement.querySelector('#nav-home');
    if (previsionsTab) {
      this.initialContentHeight = previsionsTab.offsetHeight;
      this.modalContentElement.style.minHeight = `${this.initialContentHeight +20 }px`;
    }
  }

  addTabListeners() {
    const tabs = this.modalElement.querySelectorAll('[data-toggle="tab"]');
    tabs.forEach(tab => {
      tab.addEventListener('shown.bs.tab', () => {
        this.adjustModalHeight();
      });
    });
  }

  adjustModalHeight() {
    const activeTab = this.modalElement.querySelector('.tab-pane.active');
    if (activeTab) {
      const contentHeight = Math.max(activeTab.offsetHeight, this.initialContentHeight);
      this.modalContentElement.style.minHeight = `${contentHeight}px`;
    }
  }

  // Nouvelle méthode séparée pour l'animation de la carte
  animateMapToSpot(spotCoordinates) {
    const lat = parseFloat(spotCoordinates.dataset.latitude);
    const lng = parseFloat(spotCoordinates.dataset.longitude);

    // Récupérer le zoom actuel
    const currentZoom = window.map.getZoom();

    // Utiliser le nouveau calcul de position optimisé
    const pixelPoint = window.map.project([lat, lng], currentZoom);
    const optimalOffset = this.calculateOptimalMapPosition();
    pixelPoint.y += optimalOffset;
    const offsetPoint = window.map.unproject(pixelPoint, currentZoom);

    // Animer la carte avec des options optimisées pour la fluidité
    window.map.flyTo(offsetPoint, currentZoom, {
      animate: true,
      duration: 0.8, // Légèrement plus rapide
      easeLinearity: 0.15, // Animation plus fluide
      noMoveStart: false // Permet les événements pendant l'animation
    });
  }

  // Méthode simple pour calculer l'offset optimal
  calculateOptimalMapPosition() {
    const isMobile = window.innerWidth <= 768;

    try {
      // Calcul précis basé sur le DOM
      const topBarHeight = this.getTopBarHeight();
      const modalHeight = this.getModalHeight();
      const availableSpace = window.innerHeight - topBarHeight - modalHeight;

      // console.log('🎯 DOM calculation:', {
      //   windowHeight: window.innerHeight,
      //   topBarHeight,
      //   modalHeight,
      //   availableSpace,
      //   isMobile
      // });

      // Utiliser l'espace disponible réel pour calculer l'offset
      if (availableSpace > 0) {
        if (isMobile) {
          // Sur mobile : centré dans l'espace disponible
          const offset = 70 + availableSpace * 0.5;
          // console.log('📱 Mobile offset:', offset);
          return offset;
        } else {
          // Sur desktop : centré dans l'espace disponible
          const offset = -100 +availableSpace * 0.5;
          //console.log('🖥️ Desktop offset:', offset);
          return offset;
        }
      } else {
        throw new Error('Espace disponible insuffisant');
      }
    } catch (error) {
      //console.warn("Fallback to simple positioning:", error);
      // Fallback avec les pourcentages simples et stables
      if (isMobile) {
        return window.innerHeight * 0.15;
      } else {
        return window.innerHeight * 0.25;
      }
    }
  }

  // Méthode pour obtenir la hauteur de la barre du haut
  getTopBarHeight() {
    const topBar = document.querySelector('.profil');
    if (topBar) {
      const height = topBar.offsetHeight;
      console.log('📏 Top bar height:', height);
      return height;
    }
    // Fallback : hauteurs typiques selon le device
    const fallbackHeight = window.innerWidth <= 768 ? 50 : 60;
    console.log('📏 Top bar height (fallback):', fallbackHeight);
    return fallbackHeight;
  }

  // Méthode pour obtenir la hauteur de la modal
  getModalHeight() {
    if (this.modalElement && this.modalElement.offsetHeight > 0) {
      const height = this.modalElement.offsetHeight;
      console.log('📐 Modal height:', height);
      return height;
    }
    // Fallback : estimation basée sur la taille d'écran
    const isMobile = window.innerWidth <= 768;
    const estimatedHeight = window.innerHeight * (isMobile ? 0.6 : 0.4);
    console.log('📐 Modal height (estimated):', estimatedHeight);
    return estimatedHeight;
  }
}
